import { layoutFrameEvent, type LayoutFrameMode } from "@tc-superpods/app-contract";
import type { AppStatus, ShellScreen } from "./types";

const warnedShapes = new Set<string>();

/**
 * Reads the frame an app asked for. A deployed remote can send anything, so the payload is untrusted: a
 * non-object, or a `frame` other than `shell` or `app`, is dropped with one warning per shape. Unknown extra
 * fields are ignored so an older shell accepts a newer app.
 */
export function parseLayoutFramePayload(payload: unknown): LayoutFrameMode | null {
  const frame = typeof payload === "object" && payload !== null ? (payload as { frame?: unknown }).frame : undefined;

  if (frame === "shell" || frame === "app") {
    return frame;
  }

  const shape = `${typeof payload}:${String(frame)}`;
  if (!warnedShapes.has(shape)) {
    warnedShapes.add(shape);
    console.warn(`[shell] ignored "${layoutFrameEvent}" with an invalid payload`, payload);
  }

  return null;
}

/**
 * An app can ask for a frame while it loads (it mounts, and may emit, before the shell marks it mounted) or after,
 * but not while it is unloading or failed.
 */
export function canAcceptFrameRequest(status: AppStatus): boolean {
  return status === "loading" || status === "mounted";
}

export interface ResolveFrameInput {
  authenticated: boolean;
  screen: ShellScreen;
  appStatus: AppStatus;
  request: LayoutFrameMode | null;
}

/** The shell frame is the default. The app frame applies only to a mounted app on the app screen. */
export function resolveFrame({ authenticated, screen, appStatus, request }: ResolveFrameInput): LayoutFrameMode {
  return authenticated && screen === "app" && appStatus === "mounted" && request === "app" ? "app" : "shell";
}
